// ─── Chin Bot Engine v2 ───────────────────────────────────────
// Real-time market analysis engine:
//   - Subscribes to shared cTrader SSE stream (spots + trendbars)
//   - Fetches historical candles on init (at least 100 per symbol)
//   - Maintains buffer array [A0, A1, ..., An] where:
//       A0 = most recently CLOSED candle (completed, nearest to current)
//       A1 = candle before A0, A2 = candle before A1, etc.
//     Each entry contains: timestamp, open, close, SMA14, SMA20,
//     SMA50, HMA50, HMA100, TMA50
//   - Forming candle (current, unclosed) is NOT in the buffer
//   - When a new candle completes (e.g. 8:59→9:00 H1), it becomes
//     the new A0; old A0 shifts to A1, old A1 shifts to A2, etc.
//
// Indicators calculated:
//   SMA14, SMA20, SMA50, HMA50, HMA100, TMA50

import { supabase } from './supabase';
import { subscribeSharedStream, type StreamEvent } from './shared-ctrader-stream';
import {
  SMA, HMA, TMA,
  parseTrendbars, mergeLiveCandle,
  type CandleData,
} from './indicators';
import { sendFormattedTelegram, TELEGRAM_CATEGORIES } from './telegram-sender';
import { getPipSize } from './pip-calculator';
import { buildSessionKey } from './session-key';
import { hydrateSymbolPipPositions } from './symbol-pip-position';
import type { ChinBotParams } from '@/types/strategy';

// ─── Types ──────────────────────────────────────────────────────

/**
 * Single entry in the buffer array.
 * Represents ONE completed candle with its indicator values
 * at the moment that candle closed.
 *
 * buffer[0] = A0 = most recently closed candle
 * buffer[1] = A1 = candle before A0
 * buffer[n] = An = nth candle before A0
 */
export interface BufferEntry {
  timestamp: number;
  open: number;
  close: number;
  sma14: number | null;
  sma20: number | null;
  sma50: number | null;
  hma50: number | null;
  hma100: number | null;
  tma50: number | null;
}

export interface SymbolBuffer {
  symbolId: number;
  symbolName: string;
  digits: number;
  /** cTrader ProtoOASymbol.pipPosition — the broker's authoritative pip digit index. */
  pipPosition: number | null;
  /** [A0, A1, ..., An] — newest completed candle first. Forming candle NOT included. */
  buffer: BufferEntry[];
  /** Internal: all candles including forming, oldest-first. Used for indicator calculation. */
  rawCandles: CandleData[];
  currentBid: number | null;
  currentAsk: number | null;
  lastTickTime: number;
  loaded: boolean;
}

export interface ChinBotSnapshot {
  symbols: Record<number, SymbolBuffer>;
  symbolIds: number[];
  connected: boolean;
  isRunning: boolean;
  selectedAccountId: string;
  lastTick: number;
  loading: boolean;
  lastError: string;
}

// ─── Constants ──────────────────────────────────────────────────

const MIN_CANDLES = 120;
const MAX_RAW_CANDLES = 500;
const TIMEFRAME_MINUTES: Record<string, number> = {
  M1: 1, M5: 5, M15: 15, M30: 30,
  H1: 60, H4: 240, D1: 1440, W1: 10080, MN: 43200,
};

/**
 * Last-resort digits guess, used ONLY when the broker's real `digits` is not
 * supplied by the caller.
 *
 * ⚠️ Price scaling depends on the BROKER's `digits` (the gateway divides the
 * raw trendbar integers by 10^digits). Guessing wrong scales every price up or
 * down, which corrupts pip distances — e.g. a JPY pair guessed as 3 digits
 * while the broker actually uses 5 makes every body ×100, and XAU/XAG ×1000.
 * Callers MUST pass the symbol list's real `digits`; this is a fallback only.
 */
function guessDigits(symbolName: string): number {
  const upper = symbolName.toUpperCase();
  if (upper.includes('JPY')) return 3;
  if (upper.includes('XAU') || upper.includes('XAG')) return 2;
  return 5;
}

// ─── Force Check Result Types ────────────────────────────────────

export interface ForceCheckTma50Result {
  detected: boolean;
  direction: 'ABOVE' | 'BELOW' | null;
  a0Close: number | null;
  a0Tma50: number | null;
  a1Close: number | null;
  a1Tma50: number | null;
  detail: string;
}

export interface ForceCheckConvergenceResult {
  smaTma: {
    detected: boolean;
    sma50: number | null;
    tma50: number | null;
    distance: number | null;
    distancePips: number | null;
    threshold: number | null;
    thresholdPips: number | null;
    detail: string;
  };
  allMa: {
    detected: boolean;
    availableCount: number;
    spread: number | null;
    spreadPips: number | null;
    threshold: number | null;
    thresholdPips: number | null;
    values: Record<string, number | null>;
    detail: string;
  };
}

export interface ForceCheckBoxResult {
  available: boolean;
  min100: number | null;
  max100: number | null;
  xPips: number;
  distancePips: {
    min100: number | null;
    max100: number | null;
  };
  nearest: { label: string; level: number; distancePips: number } | null;
  triggered: boolean;
}

export interface ForceCheckBoxVsTma50Result {
  available: boolean;
  min100: number | null;
  max100: number | null;
  tma50: number | null;
  range: number | null;
  threshold: number | null;
  distToMin: number | null;
  distToMax: number | null;
  pctToMin: number | null;
  pctToMax: number | null;
  nearest: { label: string; level: number; pct: number } | null;
  triggered: boolean;
}

export interface ForceCheckSymbolResult {
  symbolId: number;
  symbolName: string;
  digits: number;
  currentBid: number | null;
  currentAsk: number | null;
  bufferSize: number;
  bufferLoaded: boolean;
  tma50Cross: ForceCheckTma50Result;
  maConvergence: ForceCheckConvergenceResult;
  box: ForceCheckBoxResult;
  boxVsTma50: ForceCheckBoxVsTma50Result;
  checkedAt: number;
}

// ─── Engine Class ───────────────────────────────────────────────

export class ChinBotEngine {
  private accountId: string = '';
  private symbolIds: number[] = [];
  private symbolNames: Record<number, string> = {};
  private symbolDigits: Record<number, number> = {};
  private symbolPipPositions: Record<number, number | null> = {};
  private timeframe: string = 'H1';
  private buffers: Record<number, SymbolBuffer> = {};
  private running: boolean = false;
  private connected: boolean = false;
  private lastTick: number = 0;
  private loading: boolean = false;
  private lastError: string = '';
  private listeners: Set<(s: ChinBotSnapshot) => void> = new Set();
  private unsubStream: (() => void) | null = null;

  // ─── Activity Log callback ───────────────────────────────
  private onLog: ((level: 'info' | 'success' | 'warn' | 'error', symbol: string, message: string) => void) | null = null;

  setOnLog(fn: (level: 'info' | 'success' | 'warn' | 'error', symbol: string, message: string) => void): void {
    this.onLog = fn;
  }

  private log(level: 'info' | 'success' | 'warn' | 'error', symbol: string, message: string): void {
    if (this.onLog) {
      try { this.onLog(level, symbol, message); } catch { /* ignore */ }
    }
  }

  // ─── TMA50 Cross Alert State ──────────────────────────────
  private telegramEmail: string = 'admin@chin.vn';
  /** Maps symbolId → A0 timestamp (ms) that has already alerted.
   * Keyed by the COMPLETED candle being checked, so each completed candle
   * alerts at most once; the next candle automatically re-arms detection. */
  private tma50CrossAlertedTs: Record<number, number> = {};
  /** Tracks the timestamp of the last COMPLETED candle per symbol to detect new cycles */
  private lastCompletedTimestamp: Record<number, number> = {};



  // ─── MA Convergence (Tụ Tách) Alert State ─────────────────
  /** Symbols that have had Logic 1 alert (SMA50↔TMA50) in the current cycle */
  private convergenceAlertedSmaTma: Set<number> = new Set();
  /** Symbols that have had Logic 2 alert (all 6 MAs) in the current cycle */
  private convergenceAlertedAllMa: Set<number> = new Set();

  // ─── Box Alert State ──────────────────────────────────────
  /** Per-symbol box threshold (in pips). Default 10. */
  private boxPipsX: Record<number, number> = {};
  /** Per-symbol Box Telegram alert enabled (engine keeps running regardless). Default true. */
  private boxAlertEnabledMap: Record<number, boolean> = {};
  /** Per-symbol price-box levels, recomputed at :05 every hour. */
  private boxLevels: Record<number, { min100: number; max100: number }> = {};
  /** symbolId → cycle key already alerted (anti-spam: one alert per cycle). */
  private boxAlertedCycle: Record<number, number> = {};
  /** Cycle key — updated on every :05 recompute. */
  private boxCycleKey: number = 0;

  // ─── Box vs TMA50 Alert State ────────────────────────────
  /** Symbols that have alerted in the current Box-vs-TMA50 cycle (one alert per cycle). */
  private boxVsTma50AlertedCycle: Set<number> = new Set();

  // ─── Workspace Status Map ────────────────────────────────
  /** Per-symbol status from the ChinMonitor workspace UI (mẫu hình pattern key or null) */
  private symbolStatusMap: Record<number, string> = {};

  // ─── Workspace Note Map ──────────────────────────────────
  /** Per-symbol free-text note from the ChinMonitor workspace UI (Symbol Note popup). */
  private symbolNoteMap: Record<number, string> = {};

  // ─── Performance: Throttled notify & dirty tracking ──────
  /** Throttle interval for spot-event notifications (ms). Batches rapid spot ticks. */
  private static readonly SPOT_NOTIFY_MS = 300;
  /** Whether any spot data changed since the last snapshot/notify. */
  private spotDirty = false;
  /** Timer handle for the batched spot notify. */
  private spotThrottleTimer: ReturnType<typeof setTimeout> | null = null;
  /** Whether a full snapshot rebuild is needed (trendbar or structural change). */
  private snapshotDirty = true;
  /** Cached snapshot — reused across spot-only ticks to avoid object churn. */
  private cachedSnapshot: ChinBotSnapshot | null = null;

  /** Prevent concurrent init() calls from racing each other */
  private initializing = false;

  /** Throttle for skipped trendbar event log (diagnostic) */
  private _lastSkippedLog: number = 0;

  // ─── Polling Timers ──────────────────────────────────────────
  // TMA50 Cross: every 15 seconds, check if a new candle entered the buffer.
  // MA Convergence (Tụ Tách): fires at fixed checkpoints — every even hour at :30 UTC (0:30, 2:30, 4:30, ...).
  // TMA50 Force Check: fires at :05 every hour (GMT+7) — guaranteed catch regardless of stream health.
  private _tma50PollTimer: ReturnType<typeof setInterval> | null = null;
  private _convergenceTimer: ReturnType<typeof setTimeout> | null = null;
  private _tma50ForceCheckTimer: ReturnType<typeof setTimeout> | null = null;
  private _boxVsTma50Timer: ReturnType<typeof setTimeout> | null = null;

  get isRunning(): boolean { return this.running; }
  get selectedAccountId(): string { return this.accountId; }

  /** Update workspace status for a symbol (called from ChinMonitor UI) */
  setSymbolStatus(symbolId: number, status: string): void {
    this.symbolStatusMap[symbolId] = status;
  }

  /** Update workspace note for a symbol (called from ChinMonitor UI) */
  setSymbolNote(symbolId: number, note: string): void {
    this.symbolNoteMap[symbolId] = note || '';
  }

  /** Resolve a symbol's status into a human-readable Telegram label. */
  private buildStatusLabel(symId: number): string {
    const raw = this.symbolStatusMap[symId] || 'null';
    switch (raw) {
      case 'box_vs_candle': return 'Box vs Nến';
      case 'tma50_candle': return 'TMA50 + Nến xu hướng, nến định hướng';
      case 'ma_perfect': return 'MA hoàn hảo + Nến định hướng, nến bẻ gãy, nến xu hướng, V ngã';
      case 'ma_small': return 'Tập hợp MA nhỏ';
      case 'ma_large': return 'Tập hợp MA lớn + Nến định hướng, nến xu hướng';
      case 'candle_trend': return 'Nến xu hướng';
      default: return '—';
    }
  }

  /** Build the alert body: Status (always) + Note (only when present) + detail. */
  private buildMessageBody(symId: number, statusLabel: string, detail: string): string {
    const lines: string[] = [`Status: ${statusLabel}`];
    const note = (this.symbolNoteMap[symId] || '').trim();
    if (note) lines.push(`Note: ${note}`);
    if (detail.trim()) lines.push(detail);
    return lines.join('\n');
  }

  /** Update the Telegram target email — call after email is loaded from profiles */
  setTelegramEmail(email: string): void {
    if (email && email.trim()) {
      this.telegramEmail = email.trim();
      console.log(`[ChinBot] Telegram email updated → ${this.telegramEmail}`);
    }
  }

  // ─── Telegram Alert Gate ──────────────────────────────────
  // Two independent flags that control whether the engine SENDS Telegram
  // notifications when a fire happens. They do NOT stop the engine —
  // detection, anti-spam, and Activity Log keep running full-time.
  // Use case: running the bot on 2 accounts double-alerts the same fire,
  // so mute one account's Telegram while keeping its engine running.
  private tma50AlertEnabled: boolean = true;
  private convergenceAlertEnabled: boolean = true;
  private boxVsTma50AlertEnabled: boolean = true;

  setTma50AlertEnabled(enabled: boolean): void {
    this.tma50AlertEnabled = enabled;
    console.log(`[ChinBot] TMA50 Telegram alert ${enabled ? 'ENABLED' : 'MUTED'}`);
  }

  setConvergenceAlertEnabled(enabled: boolean): void {
    this.convergenceAlertEnabled = enabled;
    console.log(`[ChinBot] MA Convergence Telegram alert ${enabled ? 'ENABLED' : 'MUTED'}`);
  }

  setBoxVsTma50AlertEnabled(enabled: boolean): void {
    this.boxVsTma50AlertEnabled = enabled;
    console.log(`[ChinBot] Box vs TMA50 Telegram alert ${enabled ? 'ENABLED' : 'MUTED'}`);
  }

  setBoxAlertEnabled(symbolId: number, enabled: boolean): void {
    this.boxAlertEnabledMap[symbolId] = enabled;
    console.log(`[ChinBot] Box Telegram alert for symbol ${symbolId} ${enabled ? 'ENABLED' : 'MUTED'}`);
  }

  /** Update the per-symbol Box threshold (in pips). */
  setBoxPipsX(symbolId: number, x: number): void {
    this.boxPipsX[symbolId] = x;
  }

  // ─── Prime Anti-Spam State (startup backlog suppression) ─────
  // On engine start the anti-spam state is empty (in-memory, lost on restart).
  // Without priming, the very first detection pass fires Telegram alerts for
  // conditions that already existed BEFORE startup — e.g. a TMA50 cross whose
  // candle completed while the webapp was closed, or a price already sitting
  // inside the Box zone. That is the "backlog" spam on every device boot.
  //
  // Priming marks the CURRENT state as already-consumed so only a genuinely
  // NEW event (a new completed candle, or the next :05 Box recompute) can fire.
  // This matches: "send nothing on startup — only fire on a fresh, correctly-
  // timed event; skip stale info until the next cycle/fire."
  private primeAntiSpamState(): void {
    for (const symId of this.symbolIds) {
      const buf = this.buffers[symId];
      if (!buf) continue;

      // TMA50 cross is keyed by the completed candle (A0 timestamp). Mark the
      // current A0 as already-alerted so a stale cross (happened while off) is
      // NOT re-fired. A new completed candle re-arms detection automatically.
      if (buf.buffer.length >= 2 && buf.buffer[0].timestamp > 0) {
        this.tma50CrossAlertedTs[symId] = buf.buffer[0].timestamp;
        this.lastCompletedTimestamp[symId] = buf.buffer[0].timestamp;
      }

      // Box is keyed by the :05 recompute cycle. Mark the current cycle as
      // already-consumed so the first spot tick does NOT fire for a price that
      // was already inside the box zone before startup. The next :05 recompute
      // sets a fresh cycle key and re-arms detection.
      this.boxAlertedCycle[symId] = this.boxCycleKey;
    }

    console.log(
      `[ChinBot] 🛡️ Anti-spam primed for ${this.symbolIds.length} symbol(s) — ` +
      `startup backlog alerts suppressed`
    );
  }

  subscribe(fn: (s: ChinBotSnapshot) => void): () => void {
    this.listeners.add(fn);
    fn(this.snapshot());
    return () => { this.listeners.delete(fn); };
  }

  private snapshot(): ChinBotSnapshot {
    // Fast path: reuse cached snapshot if only spot data changed (no rebuild needed)
    if (!this.snapshotDirty && this.cachedSnapshot) {
      // Spot data mutations already updated cachedSnapshot in-place via handleSpot.
      // Only update the volatile fields that change on every tick.
      this.cachedSnapshot.lastTick = this.lastTick;
      this.cachedSnapshot.loading = this.loading;
      this.cachedSnapshot.lastError = this.lastError;
      return this.cachedSnapshot;
    }

    // Full rebuild: trendbar, init, or first call
    const snap: ChinBotSnapshot = {
      symbols: { ...this.buffers },
      symbolIds: [...this.symbolIds],
      connected: this.connected,
      isRunning: this.running,
      selectedAccountId: this.accountId,
      lastTick: this.lastTick,
      loading: this.loading,
      lastError: this.lastError,
    };

    this.cachedSnapshot = snap;
    this.snapshotDirty = false;
    this.spotDirty = false;
    return snap;
  }

  /**
   * Full (immediate) notify — used for trendbar, connection, loading, error state changes.
   * These are infrequent and should be delivered without delay.
   */
  private notify(): void {
    this.snapshotDirty = true;
    const s = this.snapshot();
    this.listeners.forEach((fn) => fn(s));
  }

  /**
   * Throttled spot notify — batches rapid spot ticks into at most one UI update
   * every SPOT_NOTIFY_MS. Spot data (bid/ask) is mutated in-place on the cached
   * snapshot, so when the timer fires the snapshot already has the latest values.
   */
  private scheduleSpotNotify(): void {
    this.spotDirty = true;

    // If a timer is already pending, the pending callback will pick up the latest data
    if (this.spotThrottleTimer !== null) return;

    this.spotThrottleTimer = setTimeout(() => {
      this.spotThrottleTimer = null;

      if (!this.spotDirty) return; // nothing changed since scheduled

      // Use fast-path snapshot (no rebuild) — spot data is already mutated in-place
      this.snapshotDirty = false;
      const s = this.snapshot();
      this.listeners.forEach((fn) => fn(s));
    }, ChinBotEngine.SPOT_NOTIFY_MS);
  }

  // ─── Build Buffer from Raw Candles ─────────────────────────────
  //
  // Takes rawCandles (oldest-first, includes forming candle at end),
  // computes SMA14/SMA20/SMA50/HMA50/HMA100/TMA50 indicators,
  // builds BufferEntry array ordered NEWEST-FIRST: [A0, A1, ..., An].
  //
  // When excludeForming=true: the last rawCandle (forming, unclosed)
  // is excluded from the buffer — it's not a completed candle yet.
  // After init (before any stream event), excludeForming=false so
  // all historical candles go into the buffer.

  private buildBuffer(candles: CandleData[], excludeForming: boolean): BufferEntry[] {
    const source = excludeForming && candles.length > 0
      ? candles.slice(0, -1)  // drop the forming candle at the end
      : candles;

    if (source.length === 0) return [];

    const closes = source.map((c) => c.close);

    const sma14 = SMA(closes, 14);
    const sma20 = SMA(closes, 20);
    const sma50 = SMA(closes, 50);
    const hma50 = HMA(closes, 50);
    const hma100 = HMA(closes, 100);
    const tma50 = TMA(closes, 50);

    const entries: BufferEntry[] = [];
    for (let i = 0; i < source.length; i++) {
      entries.push({
        timestamp: source[i].timestamp,
        open: source[i].open,
        close: source[i].close,
        sma14: sma14[i] ?? null,
        sma20: sma20[i] ?? null,
        sma50: sma50[i] ?? null,
        hma50: hma50[i] ?? null,
        hma100: hma100[i] ?? null,
        tma50: tma50[i] ?? null,
      });
    }

    // Reverse → newest first: buffer[0] = A0 (most recent closed)
    entries.reverse();
    return entries;
  }

  // ─── Initialize ──────────────────────────────────────────────

  async init(
    accountId: string,
    symbolInfos: Array<{ symbolId: number; symbolName: string; digits?: number; pipPosition?: number | null }>,
    timeframe: string = 'H1',
    _params?: ChinBotParams,
    telegramEmail?: string,
  ): Promise<void> {
    void _params;

    // Prevent concurrent init() calls from racing
    if (this.initializing) {
      console.log('[ChinBot] init() already in progress, skipping duplicate call');
      return;
    }

    // ─── Different account while engine is running? Stop old stream first ───
    if (this.running && this.accountId !== accountId) {
      console.log(`[ChinBot] Switching account: ${this.accountId} → ${accountId} — stopping old stream`);
      this.stop();
    }

    // If already running with same account AND same symbols → true no-op
    if (this.running && this.accountId === accountId) {
      const newSymbolIds = symbolInfos.map((s) => s.symbolId);
      const currentIds = new Set(this.symbolIds);
      const sameSymbols = newSymbolIds.length === this.symbolIds.length
        && newSymbolIds.every((id) => currentIds.has(id));

      if (sameSymbols) return;

      // Same account but different symbol list → restart with new symbols
      console.log(`[ChinBot] Symbol list changed (${this.symbolIds.length} → ${newSymbolIds.length}), restarting...`);
      this.stop();
    }

    this.initializing = true;
    this.accountId = accountId;
    this.timeframe = timeframe;
    this.loading = true;
    this.lastError = '';
    this.snapshotDirty = true;
    this.cachedSnapshot = null;
    this.notify();

    this.log('info', 'SYSTEM', `Chin Bot initializing: ${symbolInfos.length} symbols on ${timeframe} timeframe`);

    // Reset TMA50 cross alert state on new init
    this.tma50CrossAlertedTs = {};
    this.lastCompletedTimestamp = {};
    // Reset MA convergence alert state on new init
    this.convergenceAlertedSmaTma.clear();
    this.convergenceAlertedAllMa.clear();
    // Reset Box alert state on new init
    this.boxLevels = {};
    this.boxAlertedCycle = {};
    this.boxCycleKey = 0;
    // Reset Box vs TMA50 alert state on new init
    this.boxVsTma50AlertedCycle.clear();
    if (telegramEmail) {
      this.telegramEmail = telegramEmail;
    }

    // Build symbol maps
    // Resolve the broker's authoritative pipPosition for any symbol whose list
    // entry has it blank, so pip distances never fall back to a name-based guess.
    const resolvedInfos = await hydrateSymbolPipPositions(accountId, symbolInfos);
    this.symbolNames = {};
    this.symbolDigits = {};
    this.symbolPipPositions = {};
    for (const info of resolvedInfos) {
      this.symbolNames[info.symbolId] = info.symbolName;
      // Prefer the broker's real digits (from the symbol list) — this is what
      // the gateway uses to scale trendbar prices. Only guess when absent.
      this.symbolDigits[info.symbolId] =
        typeof info.digits === 'number' && info.digits > 0
          ? info.digits
          : guessDigits(info.symbolName);
      this.symbolPipPositions[info.symbolId] =
        typeof info.pipPosition === 'number' ? info.pipPosition : null;
    }
    this.symbolIds = resolvedInfos.map((s) => s.symbolId);

    // Fetch historical candles for all symbols
    await this.fetchAllHistorical();

    // Compute initial Box levels so realtime detection is armed immediately
    this.recomputeBoxLevels();

    // Mark the current state as "already consumed" so startup does NOT re-fire
    // stale alerts (TMA50 cross / Box) that happened while the webapp was off.
    this.primeAntiSpamState();

    this.loading = false;
    this.initializing = false;

    const loadedCount = Object.values(this.buffers).filter(b => b.loaded).length;
    const totalCount = this.symbolIds.length;
    this.log('success', 'SYSTEM', `Chin Bot ready: ${loadedCount}/${totalCount} symbols loaded with buffer`);

    this.notify();
  }

  // ─── Fetch Historical Candles ─────────────────────────────────

  private async fetchAllHistorical(): Promise<void> {
    const numId = parseInt(this.accountId, 10);
    if (isNaN(numId)) return;

    const periodMinutes = TIMEFRAME_MINUTES[this.timeframe] || 5;
    // Need enough bars to fill MIN_CANDLES — add 100% buffer to cover weekends/gaps
    const lookbackMinutes = Math.ceil(MIN_CANDLES * periodMinutes * 2.0);

    const fromTimestamp = Date.now() - lookbackMinutes * 60 * 1000;
    const toTimestamp = Date.now();

    // ─── Fetch all symbols IN PARALLEL ──────────────────────────
    const fetchPromises = this.symbolIds.map(async (symId) => {
      try {
        const digits = this.symbolDigits[symId] || 5;
        const symName = this.symbolNames[symId] || `Symbol#${symId}`;

        console.log(`[ChinBot] Fetching trendbars for ${symName} (id=${symId}) tf=${this.timeframe}`);

        const { data, error: invokeError } = await supabase.functions.invoke<{
          success: boolean;
          trendbars?: Record<string, unknown>;
          error?: string;
        }>('ctrader-gateway', {
          method: 'POST',
          body: {
            action: 'get-trendbars',
            accountId: numId,
            symbolId: symId,
            period: periodMinutes,
            fromTimestamp,
            toTimestamp,
          },
        });

        if (invokeError || !data?.success) {
          console.warn(`[ChinBot] Failed to fetch trendbars for ${symName}: ${data?.error || invokeError?.message}`);
          return { symId, symName, digits, candles: [] as CandleData[], error: true };
        }

        const candles = data.trendbars
          ? parseTrendbars(data.trendbars, digits)
          : [];

        console.log(`[ChinBot] Fetched ${candles.length} candles for ${symName}`);

        return { symId, symName, digits, candles, error: false };
      } catch (err) {
        const symName = this.symbolNames[symId] || `Symbol#${symId}`;
        console.warn(`[ChinBot] Error fetching ${symName}:`, err);
        const digits = this.symbolDigits[symId] || 5;
        return { symId, symName, digits, candles: [] as CandleData[], error: true };
      }
    });

    const results = await Promise.all(fetchPromises);

    // ─── Build buffers from fetched data ──────────────────────
    // Preserve live bid/ask/lastTick from the previous buffer so a periodic
    // re-fetch (hourly force check) doesn't briefly blank the live price.
    for (const { symId, symName, digits, candles, error } of results) {
      const previous = this.buffers[symId];
      if (error || candles.length === 0) {
        this.buffers[symId] = this.createEmptyBuffer(symId, symName, digits);
      } else {
        const buffer = this.createBuffer(symId, symName, digits, candles);
        if (previous) {
          buffer.currentBid = previous.currentBid;
          buffer.currentAsk = previous.currentAsk;
          buffer.lastTickTime = previous.lastTickTime;
        }
        this.buffers[symId] = buffer;
      }
    }
  }

  private createEmptyBuffer(symId: number, symName: string, digits: number): SymbolBuffer {
    return {
      symbolId: symId,
      symbolName: symName,
      digits,
      pipPosition: this.symbolPipPositions[symId] ?? null,
      buffer: [],
      rawCandles: [],
      currentBid: null,
      currentAsk: null,
      lastTickTime: 0,
      loaded: false,
    };
  }

  private createBuffer(
    symId: number,
    symName: string,
    digits: number,
    candles: CandleData[],
  ): SymbolBuffer {
    // At init time, all candles are completed — no forming candle yet.
    // excludeForming=false so all candles go into the buffer.
    const buf = this.buildBuffer(candles, false);

    // DO NOT set lastCompletedTimestamp from init buffer.
    // The init buffer may include a forming candle treated as completed.
    // If we set it here, the first REAL candle completion via stream will
    // have the same timestamp and checkTma50Cross will be SKIPPED.
    // Let it stay at 0 so the first stream-detected completion always fires.

    return {
      symbolId: symId,
      symbolName: symName,
      digits,
      pipPosition: this.symbolPipPositions[symId] ?? null,
      buffer: buf,
      rawCandles: candles,
      currentBid: null,
      currentAsk: null,
      lastTickTime: 0,
      loaded: buf.length >= MIN_CANDLES,
    };
  }

  // ─── TMA50 Cross Detector ────────────────────────────────────
  //
  // Checks buffer[0] (A0) vs buffer[1] (A1) for TMA50 cross:
  //   Case 1: TMA50[1] >= close[1] AND TMA50[0] <= close[0]
  //           → Price crossed ABOVE TMA50 (bullish)
  //   Case 2: TMA50[1] <= close[1] AND TMA50[0] >= close[0]
  //           → Price crossed BELOW TMA50 (bearish)
  //
  // Sends ONE Telegram alert per cross per cycle (per symbol).
  // Alert is fire-and-forget — does not block the stream.

  private checkTma50Cross(symId: number): void {
    const buf = this.buffers[symId];
    if (!buf || buf.buffer.length < 2) return;

    const a0 = buf.buffer[0]; // newest completed candle (A0)
    const a1 = buf.buffer[1]; // previous completed candle (A1)

    // Need valid TMA50 values for both candles
    if (a0.tma50 === null || a1.tma50 === null) return;

    const tma50_0 = a0.tma50;
    const tma50_1 = a1.tma50;
    const close_0 = a0.close;
    const close_1 = a1.close;

    let crossDetected = false;
    let direction: 'ABOVE' | 'BELOW' | null = null;

    // Case 1: Price crossed ABOVE TMA50 — TMA was above/at price, now below/at price
    if (tma50_1 >= close_1 && tma50_0 <= close_0) {
      crossDetected = true;
      direction = 'ABOVE';
    }
    // Case 2: Price crossed BELOW TMA50 — TMA was below/at price, now above/at price
    else if (tma50_1 <= close_1 && tma50_0 >= close_0) {
      crossDetected = true;
      direction = 'BELOW';
    }

    if (!crossDetected || !direction) return;

    // Anti-spam keyed by the COMPLETED candle (A0 timestamp): each completed
    // candle alerts at most once. A new candle (new timestamp) re-arms automatically.
    if (this.tma50CrossAlertedTs[symId] === a0.timestamp) return;

    this.tma50CrossAlertedTs[symId] = a0.timestamp;

    const symName = this.symbolNames[symId] || `Symbol#${symId}`;
    const digits = this.symbolDigits[symId] || 5;

    console.log(
      `[ChinBot] ⚡ TMA50 CROSS: ${symName} — Price crossed ${direction} TMA50 ` +
      `(close_0=${close_0.toFixed(digits)}, tma50_0=${tma50_0.toFixed(digits)}, ` +
      `close_1=${close_1.toFixed(digits)}, tma50_1=${tma50_1.toFixed(digits)})`
    );

    // Build status label
    const statusLabel = this.buildStatusLabel(symId);

    this.log('success', symName, `TMA50 CROSS ${direction}: close A0=${close_0.toFixed(digits)} vs TMA50=${tma50_0.toFixed(digits)}`);

    // ── Telegram gate: engine keeps detecting + logging, but skips sending ──
    if (!this.tma50AlertEnabled) {
      console.log(`[ChinBot] 🔕 TMA50 cross alert MUTED for ${symName} — Telegram disabled, engine still running`);
      this.log('info', symName, `TMA50 Cross ${direction} detected — Telegram MUTED (not sent)`);
      return;
    }

    // Fire-and-forget Telegram alert — never block the stream.
    // Pipe result into Activity Log so failures are visible in the UI.
    sendFormattedTelegram(
      {
        timeframe: this.timeframe,
        symbol: symName,
        signal: direction === 'ABOVE' ? 'BUY' : 'SELL',
        price: close_0,
        strategy: 'Chin Bot — TMA50 Cross',
        message: this.buildMessageBody(symId, statusLabel, ''),
      },
      { category: TELEGRAM_CATEGORIES.TMA50_CROSS, targetEmail: this.telegramEmail, dedupKey: this.buildDedupKey('tma50-cross', symId, `${a0.timestamp}:${direction}`) },
    ).then((result) => {
      if (result.success) {
        console.log(`[ChinBot] ✅ TMA50 cross alert SENT for ${symName}`);
        this.log('success', symName, `Telegram: TMA50 Cross ${direction} alert DELIVERED to ${this.telegramEmail}`);
      } else {
        const errDetail = result.error || 'Unknown error';
        console.warn(`[ChinBot] ⚠️ TMA50 cross alert FAILED for ${symName}: ${errDetail}`);
        this.log('error', symName, `Telegram FAILED: TMA50 Cross ${direction} — ${errDetail}`);
      }
    });
  }

  // ─── Convergence Cycle Key ────────────────────────────────────
  // Returns the timestamp (ms) of the most recent check point.
  // Check points = even hours at :30 (0:30, 2:30, 4:30, ..., 22:30 UTC)
  // Every 2 hours the cycle resets.

  private getCurrentCycleKey(now: Date): number {
    const hour = now.getUTCHours();
    const minute = now.getUTCMinutes();

    let cycleHour = hour;
    // If odd hour, go back to previous even hour
    if (hour % 2 !== 0) {
      cycleHour = hour - 1;
    }
    // If even hour but before :30, the current even hour's checkpoint hasn't happened yet
    if (hour % 2 === 0 && minute < 30) {
      cycleHour = hour - 2;
    }
    if (cycleHour < 0) cycleHour += 24;

    const cycleDate = new Date(now);
    cycleDate.setUTCHours(cycleHour, 30, 0, 0);
    return cycleDate.getTime();
  }

  // ─── Cross-Device + Cross-Account Dedup (Event ID) ────────────
  // A stable "event id" per MARKET event so that ANY device or ANY
  // account detecting the SAME event (same symbol + same cycle bucket
  // + same direction) produces the SAME dedup key. The key is built
  // from the market identity (symbol NAME + cycle), NOT accountId, so
  // two accounts monitoring the same symbol collapse to a single
  // Telegram alert instead of double-sending.

  private buildDedupKey(type: string, symId: number, bucket: number | string): string {
    const symName = (this.symbolNames[symId] || `sym${symId}`).toUpperCase();
    return `chin:${type}:${symName}:${bucket}`;
  }

  /** Deterministic cycle bucket for Box — start of the current GMT+7 hour. */
  private boxCycleBucket(): number {
    const GMT7_MS = 7 * 60 * 60 * 1000;
    const local = new Date(Date.now() + GMT7_MS);
    return Date.UTC(
      local.getUTCFullYear(),
      local.getUTCMonth(),
      local.getUTCDate(),
      local.getUTCHours(),
      0, 0, 0,
    ) - GMT7_MS;
  }

  /** Deterministic cycle bucket for Box vs TMA50 — most recent even-hour :30 GMT+7. */
  private boxVsTma50CycleBucket(): number {
    const GMT7_MS = 7 * 60 * 60 * 1000;
    const local = new Date(Date.now() + GMT7_MS);
    const evenHour = local.getUTCHours() % 2 === 0 ? local.getUTCHours() : local.getUTCHours() - 1;
    return Date.UTC(
      local.getUTCFullYear(),
      local.getUTCMonth(),
      local.getUTCDate(),
      evenHour, 30, 0, 0,
    ) - GMT7_MS;
  }

  // ─── Convergence Threshold (in price units) ───────────────────
  // Converts pip-based thresholds to raw price distance.
  // Logic 1 (SMA50↔TMA50): 3 pips forex, 50 pips XAUUSD, 15 pips XAGUSD/XTIUSD
  // Logic 2 (all 6 MAs):    8 pips forex, 100 pips XAUUSD, 30 pips XAGUSD/XTIUSD

  private getConvergenceThreshold(symId: number, symbolName: string, logicType: 'sma_tma' | 'all_ma'): number {
    const upper = symbolName.toUpperCase();
    const pipSize = getPipSize(symbolName, this.symbolPipPositions[symId] ?? null);

    if (upper.startsWith('XAU')) {
      return pipSize * (logicType === 'sma_tma' ? 50 : 100);
    }
    if (upper.startsWith('XAG')) {
      return pipSize * (logicType === 'sma_tma' ? 15 : 30);
    }
    if (upper.startsWith('XTI') || upper.includes('OIL') || upper.startsWith('WTI') || upper.startsWith('CL')) {
      return pipSize * (logicType === 'sma_tma' ? 15 : 30);
    }
    // Default forex
    return pipSize * (logicType === 'sma_tma' ? 3 : 8);
  }

  // ─── MA Convergence Detector (Tụ Tách) ────────────────────────
  //
  // Logic 1: SMA50(0) vs TMA50(0) converging
  //   If |SMA50 - TMA50| ≤ threshold → SMA50 & TMA50 đang tụ nhau
  //
  // Logic 2: All 6 MAs (SMA14, SMA20, SMA50, HMA50, HMA100, TMA50) converging
  //   If max(all 6) - min(all 6) ≤ threshold → các đường MA tụ nhau hoàn hảo
  //
  // Sends ONE Telegram alert per convergence type per symbol per cycle.
  // Alert is fire-and-forget — does not block the stream.

  private checkMaConvergence(symId: number): void {
    const buf = this.buffers[symId];
    if (!buf || buf.buffer.length === 0) return;

    const a0 = buf.buffer[0];
    const symName = this.symbolNames[symId] || `Symbol#${symId}`;
    const digits = this.symbolDigits[symId] || 5;
    const pipSize = getPipSize(symName, this.symbolPipPositions[symId] ?? null);

    // ── Logic 1: SMA50 ↔ TMA50 convergence ──
    if (a0.sma50 !== null && a0.tma50 !== null && !this.convergenceAlertedSmaTma.has(symId)) {
      const threshold1 = this.getConvergenceThreshold(symId, symName, 'sma_tma');
      const dist = Math.abs(a0.sma50 - a0.tma50);

      if (dist <= threshold1) {
        this.convergenceAlertedSmaTma.add(symId);

        console.log(
          `[ChinBot] ⚡ MA CONVERGENCE (SMA50↔TMA50): ${symName} — ` +
          `dist=${dist.toFixed(digits)} ≤ threshold=${threshold1.toFixed(digits)} ` +
          `(${(dist / pipSize).toFixed(1)} pips)`
        );

        // Build status label
        const statusLabel1 = this.buildStatusLabel(symId);

        this.log('success', symName, `MA Convergence (SMA50↔TMA50): ${(dist / pipSize).toFixed(1)} pips ≤ ${(threshold1 / pipSize).toFixed(1)} pips threshold`);

        if (this.convergenceAlertEnabled) {
          sendFormattedTelegram(
            {
              timeframe: this.timeframe,
              symbol: symName,
              signal: 'NEUTRAL',
              price: a0.close,
              strategy: 'Chin Bot — MA Convergence (SMA50 ↔ TMA50)',
              message: this.buildMessageBody(symId, statusLabel1, ''),
            },
            { category: TELEGRAM_CATEGORIES.MA_CONVERGENCE, targetEmail: this.telegramEmail, dedupKey: this.buildDedupKey('conv-sma-tma', symId, this.getCurrentCycleKey(new Date())) },
          ).then((result) => {
            if (result.success) {
              console.log(`[ChinBot] ✅ SMA50↔TMA50 convergence alert SENT for ${symName}`);
              this.log('success', symName, `Telegram: MA Convergence SMA50↔TMA50 alert DELIVERED`);
            } else {
              const errDetail = result.error || 'Unknown error';
              console.warn(`[ChinBot] ⚠️ SMA50↔TMA50 convergence alert FAILED for ${symName}: ${errDetail}`);
              this.log('error', symName, `Telegram FAILED: MA Convergence SMA50↔TMA50 — ${errDetail}`);
            }
          });
        } else {
          console.log(`[ChinBot] 🔕 SMA50↔TMA50 convergence alert MUTED for ${symName} — Telegram disabled, engine still running`);
          this.log('info', symName, 'MA Convergence SMA50↔TMA50 detected — Telegram MUTED (not sent)');
        }
      }
    }

    // ── Logic 2: All 6 MAs convergence (perfect convergence) ──
    const maValues: number[] = [];
    if (a0.sma14 !== null) maValues.push(a0.sma14);
    if (a0.sma20 !== null) maValues.push(a0.sma20);
    if (a0.sma50 !== null) maValues.push(a0.sma50);
    if (a0.hma50 !== null) maValues.push(a0.hma50);
    if (a0.hma100 !== null) maValues.push(a0.hma100);
    if (a0.tma50 !== null) maValues.push(a0.tma50);

    if (maValues.length === 6 && !this.convergenceAlertedAllMa.has(symId)) {
      const threshold2 = this.getConvergenceThreshold(symId, symName, 'all_ma');
      const maxVal = Math.max(...maValues);
      const minVal = Math.min(...maValues);
      const spread = maxVal - minVal;

      if (spread <= threshold2) {
        this.convergenceAlertedAllMa.add(symId);

        console.log(
          `[ChinBot] ⚡ MA CONVERGENCE (All 6): ${symName} — ` +
          `spread=${spread.toFixed(digits)} ≤ threshold=${threshold2.toFixed(digits)} ` +
          `(${(spread / pipSize).toFixed(1)} pips)`
        );

        // Build status label
        const statusLabel2 = this.buildStatusLabel(symId);

        this.log('success', symName, `Perfect MA Convergence (All 6): spread=${(spread / pipSize).toFixed(1)} pips ≤ ${(threshold2 / pipSize).toFixed(1)} pips`);

        if (this.convergenceAlertEnabled) {
          sendFormattedTelegram(
            {
              timeframe: this.timeframe,
              symbol: symName,
              signal: 'NEUTRAL',
              price: a0.close,
              strategy: 'Chin Bot — Perfect MA Convergence',
              message: this.buildMessageBody(symId, statusLabel2, ''),
            },
            { category: TELEGRAM_CATEGORIES.PERFECT_CONVERGENCE, targetEmail: this.telegramEmail, dedupKey: this.buildDedupKey('conv-all', symId, this.getCurrentCycleKey(new Date())) },
          ).then((result) => {
            if (result.success) {
              console.log(`[ChinBot] ✅ Perfect MA convergence alert SENT for ${symName}`);
              this.log('success', symName, `Telegram: Perfect MA Convergence alert DELIVERED`);
            } else {
              const errDetail = result.error || 'Unknown error';
              console.warn(`[ChinBot] ⚠️ Perfect MA convergence alert FAILED for ${symName}: ${errDetail}`);
              this.log('error', symName, `Telegram FAILED: Perfect MA Convergence — ${errDetail}`);
            }
          });
        } else {
          console.log(`[ChinBot] 🔕 Perfect MA convergence alert MUTED for ${symName} — Telegram disabled, engine still running`);
          this.log('info', symName, 'Perfect MA Convergence detected — Telegram MUTED (not sent)');
        }
      }
    }
  }

  // ─── Box Detector ────────────────────────────────────────────
  // Every hour at :05 (GMT+7), recomputes per-symbol price-box levels
  // from the 100 most recent CLOSED candles:
  //   Min100 / Max100 = min/max close of the 100 most recent closed candles
  // Realtime (tick-by-tick): compares the current bid against these 2
  // levels. If the distance ≤ x pips (per-symbol, default 10), sends ONE
  // Telegram alert per hour cycle (re-armed each :05 recompute).

  private recomputeBoxLevels(): void {
    this.boxCycleKey = Date.now();

    for (const symId of this.symbolIds) {
      const buf = this.buffers[symId];
      if (!buf || buf.buffer.length < 100) {
        delete this.boxLevels[symId];
        continue;
      }

      const closes100 = buf.buffer.slice(0, 100).map((b) => b.close);

      this.boxLevels[symId] = {
        min100: Math.min(...closes100),
        max100: Math.max(...closes100),
      };
    }

    console.log(`[ChinBot] 📦 Box levels recomputed for ${Object.keys(this.boxLevels).length} symbol(s)`);
  }

  /** Realtime box check — called on every spot tick. */
  private checkBoxRealtime(symId: number): void {
    const levels = this.boxLevels[symId];
    if (!levels) return;

    // Already alerted this hour cycle? (anti-spam — one alert per symbol per cycle)
    if (this.boxAlertedCycle[symId] === this.boxCycleKey) return;

    const buf = this.buffers[symId];
    if (!buf || buf.currentBid === null) return;

    const bid = buf.currentBid;
    const symName = this.symbolNames[symId] || `Symbol#${symId}`;
    const digits = this.symbolDigits[symId] || 5;
    const pipSize = getPipSize(symName, this.symbolPipPositions[symId] ?? null);
    const x = this.boxPipsX[symId] ?? 10;
    const threshold = x * pipSize;
    const statusLabel = this.buildStatusLabel(symId);

    const targets: Array<{ label: string; level: number }> = [
      { label: 'Min100', level: levels.min100 },
      { label: 'Max100', level: levels.max100 },
    ];

    for (const t of targets) {
      const dist = Math.abs(bid - t.level);
      if (dist > threshold) continue;

      this.boxAlertedCycle[symId] = this.boxCycleKey;
      const distPips = dist / pipSize;

      console.log(
        `[ChinBot] 📦 BOX: ${symName} bid=${bid.toFixed(digits)} near ${t.label}=${t.level.toFixed(digits)} ` +
        `(${distPips.toFixed(1)} pips ≤ ${x} pips)`
      );

      this.log('success', symName, `Box: price near ${t.label} ${t.level.toFixed(digits)} (${distPips.toFixed(1)} pips ≤ ${x} pips)`);

      if (this.boxAlertEnabledMap[symId] === false) {
        this.log('info', symName, `Box ${t.label} detected — Telegram MUTED (not sent)`);
        return;
      }

      sendFormattedTelegram(
        {
          timeframe: this.timeframe,
          symbol: symName,
          signal: 'NEUTRAL',
          price: bid,
          strategy: 'Chin Bot — Box',
          message: this.buildMessageBody(symId, statusLabel, `Price ${bid.toFixed(digits)} near ${t.label} ${t.level.toFixed(digits)} (${distPips.toFixed(1)} pips ≤ ${x} pips)`),
        },
        { category: TELEGRAM_CATEGORIES.BOX, targetEmail: this.telegramEmail, dedupKey: this.buildDedupKey('box', symId, `${this.boxCycleBucket()}:${t.label}`) },
      ).then((result) => {
        if (result.success) {
          console.log(`[ChinBot] ✅ Box ${t.label} alert SENT for ${symName}`);
          this.log('success', symName, `Telegram: Box ${t.label} alert DELIVERED`);
        } else {
          const errDetail = result.error || 'Unknown error';
          console.warn(`[ChinBot] ⚠️ Box ${t.label} alert FAILED for ${symName}: ${errDetail}`);
          this.log('error', symName, `Telegram FAILED: Box ${t.label} — ${errDetail}`);
        }
      });
      return;
    }
  }

  // ─── Box vs TMA50 Detector ────────────────────────────────
  // Every 2 hours at :30 of even hours in GMT+7 (8:30, 10:30, ...),
  // computes Min100/Max100 from the 100 most recent CLOSED candles,
  // then compares the latest TMA50 (A0) against these two levels:
  //   range = Max100 - Min100
  //   threshold = 25% × range
  //   If |TMA50 - Min100| ≤ threshold OR |TMA50 - Max100| ≤ threshold
  //     → send ONE Telegram alert per symbol per cycle.

  private checkBoxVsTma50(symId: number): void {
    const buf = this.buffers[symId];
    if (!buf || buf.buffer.length < 100) return;

    const a0 = buf.buffer[0];
    if (a0.tma50 === null) return;

    const closes100 = buf.buffer.slice(0, 100).map((b) => b.close);
    const min100 = Math.min(...closes100);
    const max100 = Math.max(...closes100);
    const range = max100 - min100;
    if (range <= 0) return;

    const tma50 = a0.tma50;
    const threshold = range * 0.25;
    const distToMin = Math.abs(tma50 - min100);
    const distToMax = Math.abs(tma50 - max100);

    const nearMin = distToMin <= threshold;
    const nearMax = distToMax <= threshold;
    if (!nearMin && !nearMax) return;

    // Anti-spam: one alert per symbol per cycle.
    if (this.boxVsTma50AlertedCycle.has(symId)) return;
    this.boxVsTma50AlertedCycle.add(symId);

    const symName = this.symbolNames[symId] || `Symbol#${symId}`;
    const digits = this.symbolDigits[symId] || 5;

    const pctToMin = (distToMin / range) * 100;
    const pctToMax = (distToMax / range) * 100;
    const nearestLabel = distToMin <= distToMax ? 'Min100' : 'Max100';
    const nearestLevel = distToMin <= distToMax ? min100 : max100;
    const nearestPct = distToMin <= distToMax ? pctToMin : pctToMax;

    const statusLabel = this.buildStatusLabel(symId);

    console.log(
      `[ChinBot] 📦⚡ BOX vs TMA50: ${symName} — TMA50=${tma50.toFixed(digits)} near ` +
      `${nearestLabel}=${nearestLevel.toFixed(digits)} (${nearestPct.toFixed(1)}% ≤ 25% of range ${range.toFixed(digits)})`
    );

    this.log('success', symName, `Box vs TMA50: TMA50 near ${nearestLabel} (${nearestPct.toFixed(1)}% of range ≤ 25% threshold)`);

    if (!this.boxVsTma50AlertEnabled) {
      console.log(`[ChinBot] 🔕 Box vs TMA50 alert MUTED for ${symName} — Telegram disabled, engine still running`);
      this.log('info', symName, 'Box vs TMA50 detected — Telegram MUTED (not sent)');
      return;
    }

    sendFormattedTelegram(
      {
        timeframe: this.timeframe,
        symbol: symName,
        signal: 'NEUTRAL',
        price: a0.close,
        strategy: 'Chin Bot — Box vs TMA50',
        message: this.buildMessageBody(symId, statusLabel, `TMA50 ${tma50.toFixed(digits)} near ${nearestLabel} ${nearestLevel.toFixed(digits)} (${nearestPct.toFixed(1)}% of range, threshold 25%)`),
      },
      { category: TELEGRAM_CATEGORIES.BOX_VS_TMA50, targetEmail: this.telegramEmail, dedupKey: this.buildDedupKey('box-vs-tma50', symId, this.boxVsTma50CycleBucket()) },
    ).then((result) => {
      if (result.success) {
        console.log(`[ChinBot] ✅ Box vs TMA50 alert SENT for ${symName}`);
        this.log('success', symName, 'Telegram: Box vs TMA50 alert DELIVERED');
      } else {
        const errDetail = result.error || 'Unknown error';
        console.warn(`[ChinBot] ⚠️ Box vs TMA50 alert FAILED for ${symName}: ${errDetail}`);
        this.log('error', symName, `Telegram FAILED: Box vs TMA50 — ${errDetail}`);
      }
    });
  }

  // ─── Force Check All Symbols ─────────────────────────────────
  // Runs TMA50 cross + MA convergence checks on ALL symbols
  // without sending Telegram alerts, without modifying anti-spam state.
  // Returns detailed results for each symbol so the user can verify
  // that the detection logic is working correctly.

  forceCheckAllSymbols(): ForceCheckSymbolResult[] {
    const results: ForceCheckSymbolResult[] = [];
    const now = Date.now();

    for (const symId of this.symbolIds) {
      const buf = this.buffers[symId];
      if (!buf) continue;

      const symName = this.symbolNames[symId] || `Symbol#${symId}`;
      const digits = this.symbolDigits[symId] || 5;
      const pipSize = getPipSize(symName, this.symbolPipPositions[symId] ?? null);

      // ── TMA50 Cross Check ──
      let tma50Result: ForceCheckTma50Result = {
        detected: false,
        direction: null,
        a0Close: null,
        a0Tma50: null,
        a1Close: null,
        a1Tma50: null,
        detail: '',
      };

      if (buf.buffer.length >= 2) {
        const a0 = buf.buffer[0];
        const a1 = buf.buffer[1];
        const a0Close = a0.close;
        const a1Close = a1.close;
        const a0Tma = a0.tma50;
        const a1Tma = a1.tma50;

        tma50Result = {
          detected: false,
          direction: null,
          a0Close,
          a0Tma50: a0Tma,
          a1Close,
          a1Tma50: a1Tma,
          detail: '',
        };

        if (a0Tma !== null && a1Tma !== null) {
          // Case 1: Price crossed ABOVE TMA50
          if (a1Tma >= a1Close && a0Tma <= a0Close) {
            tma50Result.detected = true;
            tma50Result.direction = 'ABOVE';
            tma50Result.detail =
              `Price crossed ABOVE TMA50: A1 close=${a1Close.toFixed(digits)} ≤ TMA50=${a1Tma.toFixed(digits)} → A0 close=${a0Close.toFixed(digits)} ≥ TMA50=${a0Tma.toFixed(digits)}`;
          }
          // Case 2: Price crossed BELOW TMA50
          else if (a1Tma <= a1Close && a0Tma >= a0Close) {
            tma50Result.detected = true;
            tma50Result.direction = 'BELOW';
            tma50Result.detail =
              `Price crossed BELOW TMA50: A1 close=${a1Close.toFixed(digits)} ≥ TMA50=${a1Tma.toFixed(digits)} → A0 close=${a0Close.toFixed(digits)} ≤ TMA50=${a0Tma.toFixed(digits)}`;
          } else {
            tma50Result.detail =
              `No cross. A0 close=${a0Close.toFixed(digits)} vs TMA50=${a0Tma.toFixed(digits)}, A1 close=${a1Close.toFixed(digits)} vs TMA50=${a1Tma.toFixed(digits)}`;
          }
        } else {
          tma50Result.detail = 'Not enough TMA50 data (need 2 completed candles with valid TMA50)';
        }
      } else {
        tma50Result.detail = `Buffer too small (${buf.buffer.length} entries, need >= 2)`;
      }

      // ── MA Convergence Check ──
      const a0 = buf.buffer.length > 0 ? buf.buffer[0] : null;

      // Logic 1: SMA50 ↔ TMA50
      const threshold1 = this.getConvergenceThreshold(symId, symName, 'sma_tma');
      const threshold1Pips = threshold1 / pipSize;
      let smaTmaResult: ForceCheckConvergenceResult['smaTma'] = {
        detected: false,
        sma50: null,
        tma50: null,
        distance: null,
        distancePips: null,
        threshold: threshold1,
        thresholdPips: threshold1Pips,
        detail: '',
      };

      if (a0 && a0.sma50 !== null && a0.tma50 !== null) {
        const dist = Math.abs(a0.sma50 - a0.tma50);
        const distPips = dist / pipSize;
        const detected = dist <= threshold1;

        smaTmaResult = {
          detected,
          sma50: a0.sma50,
          tma50: a0.tma50,
          distance: dist,
          distancePips: distPips,
          threshold: threshold1,
          thresholdPips: threshold1Pips,
          detail: detected
            ? `SMA50(${a0.sma50.toFixed(digits)}) ↔ TMA50(${a0.tma50.toFixed(digits)}) distance=${distPips.toFixed(1)} pips ≤ threshold ${threshold1Pips.toFixed(1)} pips → TỤ`
            : `SMA50(${a0.sma50.toFixed(digits)}) ↔ TMA50(${a0.tma50.toFixed(digits)}) distance=${distPips.toFixed(1)} pips > threshold ${threshold1Pips.toFixed(1)} pips → TÁCH`,
        };
      } else {
        smaTmaResult.detail = a0
          ? `Missing indicator data: SMA50=${a0.sma50 !== null ? 'OK' : 'NULL'}, TMA50=${a0.tma50 !== null ? 'OK' : 'NULL'}`
          : 'Buffer empty';
      }

      // Logic 2: All 6 MAs
      const threshold2 = this.getConvergenceThreshold(symId, symName, 'all_ma');
      const threshold2Pips = threshold2 / pipSize;
      const maValues: Record<string, number | null> = {};
      if (a0) {
        maValues['SMA14'] = a0.sma14;
        maValues['SMA20'] = a0.sma20;
        maValues['SMA50'] = a0.sma50;
        maValues['HMA50'] = a0.hma50;
        maValues['HMA100'] = a0.hma100;
        maValues['TMA50'] = a0.tma50;
      }

      const validValues = Object.values(maValues).filter((v): v is number => v !== null);
      let allMaResult: ForceCheckConvergenceResult['allMa'] = {
        detected: false,
        availableCount: validValues.length,
        spread: null,
        spreadPips: null,
        threshold: threshold2,
        thresholdPips: threshold2Pips,
        values: maValues,
        detail: '',
      };

      if (validValues.length === 6) {
        const maxVal = Math.max(...validValues);
        const minVal = Math.min(...validValues);
        const spread = maxVal - minVal;
        const spreadPips = spread / pipSize;
        const detected = spread <= threshold2;

        allMaResult = {
          detected,
          availableCount: 6,
          spread,
          spreadPips,
          threshold: threshold2,
          thresholdPips: threshold2Pips,
          values: maValues,
          detail: detected
            ? `All 6 MAs range: max-min=${spreadPips.toFixed(1)} pips ≤ threshold ${threshold2Pips.toFixed(1)} pips → SIÊU TỤ`
            : `All 6 MAs range: max-min=${spreadPips.toFixed(1)} pips > threshold ${threshold2Pips.toFixed(1)} pips → CHƯA TỤ`,
        };
      } else {
        allMaResult.detail = `Only ${validValues.length}/6 MA values available (need all 6)`;
      }

      // ── Box Check (read-only; does not touch anti-spam/levels state) ──
      const closesAll = buf.buffer.slice(0, 100).map((b) => b.close);
      let boxResult: ForceCheckBoxResult = {
        available: false,
        min100: null,
        max100: null,
        xPips: this.boxPipsX[symId] ?? 10,
        distancePips: { min100: null, max100: null },
        nearest: null,
        triggered: false,
      };

      if (closesAll.length >= 100) {
        const min100 = Math.min(...closesAll);
        const max100 = Math.max(...closesAll);
        const x = this.boxPipsX[symId] ?? 10;

        const distancePips: ForceCheckBoxResult['distancePips'] = {
          min100: null,
          max100: null,
        };
        let nearest: ForceCheckBoxResult['nearest'] = null;
        let triggered = false;

        if (buf.currentBid !== null) {
          const bid = buf.currentBid;
          const levels: Array<{ key: 'min100' | 'max100'; label: string; level: number }> = [
            { key: 'min100', label: 'Min100', level: min100 },
            { key: 'max100', label: 'Max100', level: max100 },
          ];

          let minDistPips = Infinity;
          for (const l of levels) {
            const distPips = Math.abs(bid - l.level) / pipSize;
            distancePips[l.key] = distPips;
            if (distPips < minDistPips) {
              minDistPips = distPips;
              nearest = { label: l.label, level: l.level, distancePips: distPips };
            }
            if (distPips <= x) triggered = true;
          }
        }

        boxResult = {
          available: true,
          min100,
          max100,
          xPips: x,
          distancePips,
          nearest,
          triggered,
        };
      }

      // ── Box vs TMA50 Check (read-only; does not touch anti-spam state) ──
      const closesBv = buf.buffer.slice(0, 100).map((b) => b.close);
      let boxVsTma50Result: ForceCheckBoxVsTma50Result = {
        available: false,
        min100: null,
        max100: null,
        tma50: null,
        range: null,
        threshold: null,
        distToMin: null,
        distToMax: null,
        pctToMin: null,
        pctToMax: null,
        nearest: null,
        triggered: false,
      };

      if (closesBv.length >= 100) {
        const min100Bv = Math.min(...closesBv);
        const max100Bv = Math.max(...closesBv);
        const rangeBv = max100Bv - min100Bv;
        const tma50Bv = a0?.tma50 ?? null;

        boxVsTma50Result = {
          available: true,
          min100: min100Bv,
          max100: max100Bv,
          tma50: tma50Bv,
          range: rangeBv,
          threshold: null,
          distToMin: null,
          distToMax: null,
          pctToMin: null,
          pctToMax: null,
          nearest: null,
          triggered: false,
        };

        if (rangeBv > 0 && tma50Bv !== null) {
          const thresholdBv = rangeBv * 0.25;
          const distToMinBv = Math.abs(tma50Bv - min100Bv);
          const distToMaxBv = Math.abs(tma50Bv - max100Bv);
          const pctToMinBv = (distToMinBv / rangeBv) * 100;
          const pctToMaxBv = (distToMaxBv / rangeBv) * 100;
          const triggeredBv = distToMinBv <= thresholdBv || distToMaxBv <= thresholdBv;
          const nearestBv = distToMinBv <= distToMaxBv
            ? { label: 'Min100', level: min100Bv, pct: pctToMinBv }
            : { label: 'Max100', level: max100Bv, pct: pctToMaxBv };

          boxVsTma50Result = {
            ...boxVsTma50Result,
            threshold: thresholdBv,
            distToMin: distToMinBv,
            distToMax: distToMaxBv,
            pctToMin: pctToMinBv,
            pctToMax: pctToMaxBv,
            nearest: nearestBv,
            triggered: triggeredBv,
          };
        }
      }

      results.push({
        symbolId: symId,
        symbolName: symName,
        digits,
        currentBid: buf.currentBid,
        currentAsk: buf.currentAsk,
        bufferSize: buf.buffer.length,
        bufferLoaded: buf.loaded,
        tma50Cross: tma50Result,
        maConvergence: {
          smaTma: smaTmaResult,
          allMa: allMaResult,
        },
        box: boxResult,
        boxVsTma50: boxVsTma50Result,
        checkedAt: now,
      });
    }

    console.log(`[ChinBot] Force check complete: ${results.length} symbols checked at ${new Date(now).toISOString()}`);
    return results;
  }

  // ─── Refresh Historical Buffers ──────────────────────────────
  // Public method to re-fetch historical candles for all symbols.
  // Useful when buffers are empty/too small and user wants to force-check.

  async refreshBuffers(): Promise<void> {
    if (this.symbolIds.length === 0) return;
    this.loading = true;
    this.lastError = '';
    this.notify();

    await this.fetchAllHistorical();

    this.loading = false;
    this.notify();
  }

  // ─── Stream Event Handler ─────────────────────────────────────

  private onStreamEvent = (evt: StreamEvent): void => {
    if (!this.running) {
      // Log skipped events every 30s to help diagnose "no handleTrendbar" issues
      if (evt.type === 'trendbar') {
        const now = Date.now();
        if (!this._lastSkippedLog || now - this._lastSkippedLog > 30000) {
          this._lastSkippedLog = now;
          console.warn(`[ChinBot] ⚠️ SKIPPED trendbar event — engine is NOT running (running=${this.running})`);
        }
      }
      return;
    }

    if (evt.type === 'spot') {
      this.handleSpot(evt);
    } else if (evt.type === 'trendbar') {
      this.handleTrendbar(evt);
    } else if (evt.type === 'connected') {
      this.connected = true;
      this.log('success', 'SYSTEM', 'Stream CONNECTED — receiving real-time data');
      this.notify();
    } else if (evt.type === 'disconnected' || evt.type === 'error') {
      this.connected = false;
      this.log('warn', 'SYSTEM', `Stream ${evt.type === 'disconnected' ? 'DISCONNECTED' : 'ERROR'} — waiting for reconnect...`);
      this.notify();
    }
  };

  private handleSpot(evt: StreamEvent): void {
    if (evt.symbolId === null || evt.symbolId === undefined) return;

    const buf = this.buffers[evt.symbolId];
    if (!buf) return;

    if (evt.bid !== null && evt.bid !== undefined) buf.currentBid = evt.bid;
    if (evt.ask !== null && evt.ask !== undefined) buf.currentAsk = evt.ask;
    buf.lastTickTime = evt.receivedAt || Date.now();

    // ── NOTE: TMA50 Cross detection is ONLY done on completed candles (A0 vs A1) ──
    // in checkTma50Cross(), which is called from handleTrendbar when a new candle
    // timestamp is detected. No spot/real-time cross detection here — by design.

    // Box detection runs tick-by-tick against the latest price-box levels.
    this.checkBoxRealtime(evt.symbolId);

    this.lastTick = Date.now();
    this.scheduleSpotNotify();
  }

  private handleTrendbar(evt: StreamEvent): void {
    if (evt.symbolId === null || evt.symbolId === undefined) return;

    const buf = this.buffers[evt.symbolId];
    if (!buf) return;

    const symName = this.symbolNames[evt.symbolId] || `#${evt.symbolId}`;

    const newCandle: CandleData = {
      timestamp: evt.timestamp || Date.now(),
      open: evt.open ?? 0,
      high: evt.high ?? 0,
      low: evt.low ?? 0,
      close: evt.close ?? 0,
      volume: evt.volume ?? undefined,
    };

    if (newCandle.open === 0 && newCandle.close === 0) {
      console.warn(`[ChinBot] ⚠️ handleTrendbar SKIP — zero-price candle for ${symName} ts=${newCandle.timestamp}`);
      return;
    }

    const prevRawLen = buf.rawCandles.length;
    const merged = mergeLiveCandle(buf.rawCandles, newCandle);

    if (merged.length > MAX_RAW_CANDLES) {
      buf.rawCandles = merged.slice(-MAX_RAW_CANDLES);
    } else {
      buf.rawCandles = merged;
    }

    const needsRebuild = merged.length > prevRawLen || prevRawLen === 0;

    const tsMs = newCandle.timestamp;
    const tsDate = new Date(tsMs > 1e10 ? tsMs : tsMs * 60000).toISOString();

    console.log(
      `[ChinBot] 📊 TRENDBAR: ${symName} ts=${tsMs} (${tsDate}) ` +
      `rawTs=${evt.timestamp} prevLen=${prevRawLen} mergedLen=${merged.length} ` +
      `needsRebuild=${needsRebuild} close=${newCandle.close.toFixed(5)} ` +
      `running=${this.running} alertedTs=${this.tma50CrossAlertedTs[evt.symbolId!] || 0}`
    );

    if (needsRebuild) {
      // ─── Save old A0 state BEFORE rebuild ──────────────────
      // When the forming candle (e.g. 9:00 H1) arrives via stream, it triggers
      // a rebuild. buildBuffer drops the forming candle, so buffer[0] (A0) stays
      // the same candle (e.g. 8:00). But its CLOSE PRICE may have been updated
      // by stream events during the hour — the candle is now truly complete.
      // We compare old vs new A0 to detect this "candle finalization" case.
      const oldA0 = buf.buffer.length > 0
        ? { ts: buf.buffer[0].timestamp, close: buf.buffer[0].close }
        : null;

      buf.buffer = this.buildBuffer(buf.rawCandles, true);
      buf.loaded = buf.buffer.length >= MIN_CANDLES;

      const newA0 = buf.buffer.length > 0
        ? { ts: buf.buffer[0].timestamp, close: buf.buffer[0].close }
        : null;

      const newTimestamp = newA0?.ts ?? 0;
      const prevTimestamp = this.lastCompletedTimestamp[evt.symbolId] || 0;

      // ─── Detect TWO cases that warrant cross re-check ──────────
      // Case A: A0 TIMESTAMP changed → NEW completed candle entered buffer.
      //   Example: 10:00 forming arrives → 9:00 enters buffer as A0.
      // Case B: A0 CLOSE changed (same timestamp) → existing candle was
      //   UPDATED (stream finalized its close price after the hour ended).
      //   Example: 8:00 candle close was 1.0850 at init; stream updated it
      //   to 1.0855 during the hour → close changed when 9:00 forming arrives.
      const a0TimestampChanged = newTimestamp > 0 && newTimestamp !== (oldA0?.ts ?? 0);
      const a0CloseChanged = oldA0 !== null && newA0 !== null
        && oldA0.ts === newA0.ts
        && Math.abs(oldA0.close - newA0.close) > 1e-9;

      const shouldCheckCross = (a0TimestampChanged || a0CloseChanged) && newTimestamp > 0;

      console.log(
        `[ChinBot] 🔄 REBUILD: ${symName} ` +
        `oldA0=(ts=${oldA0?.ts}, close=${oldA0?.close?.toFixed(buf.digits || 5)}) ` +
        `newA0=(ts=${newA0?.ts}, close=${newA0?.close?.toFixed(buf.digits || 5)}) ` +
        `tsChanged=${a0TimestampChanged} closeChanged=${a0CloseChanged} ` +
        `shouldCheck=${shouldCheckCross} prevTs=${prevTimestamp} bufferLen=${buf.buffer.length}`
      );

      if (shouldCheckCross) {
        // ─── New candle data! Reset anti-spam and fire cross detection ───
        const candleTime = new Date(newTimestamp > 1e10 ? newTimestamp : newTimestamp * 60_000).toISOString();
        console.log(
          `[ChinBot] 🎯 TRENDBAR DETECTED: ${symName} A0 updated — ` +
          `${candleTime.slice(0, 19).replace('T', ' ')} ` +
          `(reason: ${a0TimestampChanged ? 'timestamp changed' : 'close finalized'})`
        );
        this.log('info', symName,
          `A0 updated via stream: ${candleTime.slice(0, 19).replace('T', ' ')} ` +
          `(${a0TimestampChanged ? 'new candle' : 'close finalized'}, buffer: ${buf.buffer.length} entries)`
        );

        // New candle data — run cross detection immediately. The timestamp-keyed
        // anti-spam inside checkTma50Cross handles dedup automatically.
        this.lastCompletedTimestamp[evt.symbolId] = newTimestamp;
        this.checkTma50Cross(evt.symbolId);

        // Also check MA convergence on the updated buffer
        if (buf.loaded) {
          this.checkMaConvergence(evt.symbolId);
        }
      }
    }

    buf.lastTickTime = evt.receivedAt || Date.now();
    this.lastTick = Date.now();
    this.notify();
  }

  // ─── Polling: TMA50 Cross Detection ──────────────────────────
  // Runs every 15 seconds. Checks if buffer[0].timestamp has changed
  // since the last check — if so, a new candle has entered the buffer
  // and we trigger the cross detection. This is immune to stream event
  // delays/drops because it polls the buffer directly.

  private _pollTma50Check(): void {
    if (!this.running) return;

    for (const symId of this.symbolIds) {
      const buf = this.buffers[symId];
      if (!buf || !buf.loaded || buf.buffer.length < 2) continue;

      // Timestamp-keyed anti-spam inside checkTma50Cross dedups per completed
      // candle, so calling it every 15s is safe — it only fires when a NEW
      // completed candle (new A0 timestamp) shows a cross.
      this.checkTma50Cross(symId);
    }
  }

  // ─── Polling: MA Convergence (Tụ Tách) Cycle ─────────────────
  // Runs every 2 hours. Resets anti-spam flags and checks all
  // loaded symbols for MA convergence using the current buffer.
  // No need to wait for candle completion — just compute from
  // whatever buffer data is available.

  private _runConvergenceCycle(): void {
    if (!this.running) return;

    console.log('[ChinBot] 🔄 Convergence cycle running...');
    this.log('info', 'SYSTEM', `MA Convergence cycle — checking ${this.symbolIds.length} symbols`);

    // Reset anti-spam for ALL symbols — new cycle, fresh state
    this.convergenceAlertedSmaTma.clear();
    this.convergenceAlertedAllMa.clear();

    let checkedCount = 0;
    for (const symId of this.symbolIds) {
      const buf = this.buffers[symId];
      if (buf && buf.loaded) {
        this.checkMaConvergence(symId);
        checkedCount++;
      }
    }

    console.log(`[ChinBot] 🔄 Convergence cycle done — ${checkedCount} symbols checked`);
    this.log('info', 'SYSTEM', `MA Convergence cycle complete: ${checkedCount}/${this.symbolIds.length} symbols checked`);
  }

  // ─── Polling: Box vs TMA50 Cycle ─────────────────────────
  // Runs every 2 hours at :30 of even hours in GMT+7 (8:30, 10:30, ...).
  // Resets anti-spam and checks all loaded symbols using the current buffer.

  private _runBoxVsTma50Cycle(): void {
    if (!this.running) return;

    console.log('[ChinBot] 🔄 Box vs TMA50 cycle running...');
    this.log('info', 'SYSTEM', `Box vs TMA50 cycle — checking ${this.symbolIds.length} symbols`);

    // Reset anti-spam for ALL symbols — new cycle, fresh state
    this.boxVsTma50AlertedCycle.clear();

    let checkedCount = 0;
    for (const symId of this.symbolIds) {
      const buf = this.buffers[symId];
      if (buf && buf.loaded) {
        this.checkBoxVsTma50(symId);
        checkedCount++;
      }
    }

    console.log(`[ChinBot] 🔄 Box vs TMA50 cycle done — ${checkedCount} symbols checked`);
    this.log('info', 'SYSTEM', `Box vs TMA50 cycle complete: ${checkedCount}/${this.symbolIds.length} symbols checked`);
  }

  // ─── Schedule Next Box vs TMA50 Cycle ─────────────────────
  // Fires at even hours :30 in GMT+7 (8:30, 10:30, 12:30, ... local time).
  // Even GMT+7 hours map to odd UTC hours (1:30, 3:30, 5:30, ... UTC).

  private _scheduleNextBoxVsTma50Cycle(): void {
    const GMT7_MS = 7 * 60 * 60 * 1000;
    const now = Date.now();

    // Current wall-clock time in GMT+7
    const localNow = now + GMT7_MS;
    const d = new Date(localNow);

    // Find the next even local hour (0, 2, 4, ..., 22) at :30:00.000
    const localH = d.getUTCHours();
    const nextEvenHour = localH % 2 === 0 ? localH : localH + 1;

    // Date.UTC normalizes hour>=24 into the next day automatically.
    let targetLocal = Date.UTC(
      d.getUTCFullYear(),
      d.getUTCMonth(),
      d.getUTCDate(),
      nextEvenHour, 30, 0, 0,
    );

    // If this even-hour :30 already passed, advance 2 hours.
    if (targetLocal <= localNow) {
      targetLocal += 2 * 60 * 60 * 1000;
    }

    const targetUtc = targetLocal - GMT7_MS;
    const delayMs = targetUtc - now;
    const targetLocalDisplay = new Date(targetUtc + GMT7_MS);

    console.log(
      `[ChinBot] ⏰ Next Box vs TMA50 checkpoint: ${targetLocalDisplay.toISOString().slice(0, 19).replace('T', ' ')} GMT+7 ` +
      `(in ${Math.round(delayMs / 1000 / 60)} min ${Math.round((delayMs / 1000) % 60)} sec)`
    );

    this._boxVsTma50Timer = setTimeout(() => {
      this._runBoxVsTma50Cycle();
      this._scheduleNextBoxVsTma50Cycle();
    }, delayMs);
  }

  // ─── Polling: TMA50 Force Check at :05 every hour (GMT+7) ────
  // Guaranteed check — at :05 the new candle (that closed at :00)
  // is definitely in the buffer. Does NOT depend on stream trendbar
  // events being timely. Works alongside the 15s fast poll.

  private async _runTma50ForceCheck(): Promise<void> {
    if (!this.running) return;

    console.log('[ChinBot] 🔄 TMA50 Force Check (:05 GMT+7) — re-fetching fresh candles...');

    // ─── Re-fetch historical candles so the buffer ALWAYS holds the latest
    // completed candle, even if the stream missed/delayed the forming-candle
    // trendbar events. This is the guaranteed catch the stream cannot provide.
    await this.fetchAllHistorical();

    // Recompute Box levels + re-arm the hourly Box alert cycle.
    this.recomputeBoxLevels();

    // Invalidate the cached snapshot so the UI reflects the fresh buffer.
    this.notify();

    let crossFired = 0;

    for (const symId of this.symbolIds) {
      const buf = this.buffers[symId];
      if (!buf || !buf.loaded || buf.buffer.length < 2) continue;

      const currentTs = buf.buffer[0].timestamp;
      const symName = this.symbolNames[symId] || `Symbol#${symId}`;
      const candleTime = new Date(currentTs > 1e10 ? currentTs : currentTs * 60_000).toISOString();

      console.log(
        `[ChinBot] 🎯 FORCE CHECK: ${symName} A0=${candleTime.slice(0, 19).replace('T', ' ')} ` +
        `(buffer=${buf.buffer.length} candles, previouslyAlertedTs=${this.tma50CrossAlertedTs[symId] || 0})`
      );

      const wasAlertedTs = this.tma50CrossAlertedTs[symId] || 0;
      this.checkTma50Cross(symId);
      const nowAlertedTs = this.tma50CrossAlertedTs[symId] || 0;
      if (nowAlertedTs !== wasAlertedTs && nowAlertedTs > 0) {
        crossFired++;
      }
    }

    console.log(`[ChinBot] 🔄 TMA50 Force Check done — ${crossFired} cross alerts fired`);
    this.log('info', 'SYSTEM', `TMA50 Force Check (:05 GMT+7): ${crossFired} cross alerts`);
  }

  /**
   * Calculate the delay (ms) until the next :05 GMT+7 checkpoint
   * and schedule _runTma50ForceCheck at that exact moment.
   */
  private _scheduleNextTma50ForceCheck(): void {
    const GMT7_MS = 7 * 60 * 60 * 1000;
    const now = Date.now();

    // Current time in GMT+7
    const localNow = now + GMT7_MS;
    const localDate = new Date(localNow);
    const localH = localDate.getUTCHours();
    const localM = localDate.getUTCMinutes();
    const localS = localDate.getUTCSeconds();
    const localMs = localDate.getUTCMilliseconds();

    // Next :05 in GMT+7
    let nextLocalH = localH;
    // If we're past :05 (minute >= 5, or exactly :05 but seconds > 0), go to next hour
    if (localM > 5 || (localM === 5 && (localS > 0 || localMs > 0))) {
      nextLocalH = (localH + 1) % 24;
    }

    // Build target date in GMT+7 then convert back to UTC
    const targetLocal = Date.UTC(
      localDate.getUTCFullYear(),
      localDate.getUTCMonth(),
      localDate.getUTCDate(),
      nextLocalH, 5, 0, 0,
    );
    let targetUtc = targetLocal - GMT7_MS;

    // If already passed (wrapped around midnight in GMT+7), add 1 day
    if (targetUtc <= now) {
      targetUtc += 24 * 60 * 60 * 1000;
    }

    const delayMs = targetUtc - now;
    const targetDate = new Date(targetUtc);
    const targetLocalDisplay = new Date(targetUtc + GMT7_MS);
    console.log(
      `[ChinBot] ⏰ Next TMA50 Force Check: ${targetLocalDisplay.toISOString().slice(0, 19).replace('T', ' ')} GMT+7 ` +
      `(${targetDate.toISOString().slice(0, 19).replace('T', ' ')} UTC) ` +
      `— in ${Math.round(delayMs / 1000 / 60)} min ${Math.round((delayMs / 1000) % 60)} sec`
    );

    this._tma50ForceCheckTimer = setTimeout(() => {
      this._runTma50ForceCheck().finally(() => {
        this._scheduleNextTma50ForceCheck(); // re-schedule for next hour
      });
    }, delayMs);
  }

  // ─── Schedule Next Convergence Cycle ─────────────────────────
  // Calculates the time until the next even-hour :30 UTC checkpoint
  // and schedules _runConvergenceCycle at that exact moment.
  // After each fire, re-schedules for the next checkpoint.

  private _scheduleNextConvergenceCycle(): void {
    const now = new Date();
    const hour = now.getUTCHours();
    const minute = now.getUTCMinutes();
    const second = now.getUTCSeconds();
    const ms = now.getUTCMilliseconds();

    // Find the next even-hour :30 UTC checkpoint
    // Checkpoints: 0:30, 2:30, 4:30, 6:30, ..., 22:30 UTC
    let nextEvenHour: number;

    const isBeforeCheckpoint = (h: number) => {
      // If we're at hour h and before :30, the checkpoint at h:30 hasn't happened yet
      if (hour === h && (minute < 30 || (minute === 30 && second === 0 && ms === 0))) {
        return true;
      }
      return hour < h;
    };

    // Try the current even hour first
    const currentEvenHour = Math.floor(hour / 2) * 2;
    if (isBeforeCheckpoint(currentEvenHour)) {
      nextEvenHour = currentEvenHour;
    } else {
      // Already past current even hour's checkpoint, go to next even hour
      nextEvenHour = currentEvenHour + 2;
      if (nextEvenHour >= 24) nextEvenHour -= 24;
    }

    // Calculate ms until nextEvenHour:30:00.000 UTC
    const target = new Date(now);
    target.setUTCHours(nextEvenHour, 30, 0, 0);

    let delayMs = target.getTime() - now.getTime();
    if (delayMs <= 0) {
      // We're exactly at the checkpoint or slightly past — fire in 2h
      delayMs = 2 * 60 * 60 * 1000;
    }

    const targetTime = target.toISOString().slice(0, 19).replace('T', ' ');
    console.log(
      `[ChinBot] ⏰ Next MA Convergence checkpoint: ${targetTime} UTC ` +
      `(in ${Math.round(delayMs / 1000 / 60)} minutes)`
    );

    this._convergenceTimer = setTimeout(() => {
      this._runConvergenceCycle();
      // Schedule the next one after this fire
      this._scheduleNextConvergenceCycle();
    }, delayMs);
  }

  // ─── Start / Stop ────────────────────────────────────────────

  start(): void {
    if (this.running) return;
    if (this.symbolIds.length === 0) return;

    this.running = true;
    this.connected = false;

    const numId = parseInt(this.accountId, 10);
    if (isNaN(numId)) return;

    // Subscribe to shared stream for spots + trendbars
    const periodMinutes = TIMEFRAME_MINUTES[this.timeframe] || 5;

    this.unsubStream = subscribeSharedStream(
      {
        id: `chin-bot__${this.accountId}`,
        symbolIds: this.symbolIds,
        periods: [periodMinutes],
        symbolDigits: this.symbolDigits,
        onEvent: this.onStreamEvent,
        onStatusChange: (_status) => {
          // status is handled via connected/disconnected events
        },
      },
      numId,
    );

    // ─── Start polling timers ──────────────────────────────────
    // TMA50 Cross: every 15s, check if new candle entered buffer
    this._tma50PollTimer = setInterval(() => this._pollTma50Check(), 15_000);
    console.log('[ChinBot] TMA50 Cross polling started — every 15s');

    // TMA50 Force Check: guaranteed catch at :05 every hour (GMT+7)
    this._scheduleNextTma50ForceCheck();

    // MA Convergence: fires at fixed checkpoints — every even hour at :30 UTC (0:30, 2:30, 4:30, ...)
    this._scheduleNextConvergenceCycle();
    console.log('[ChinBot] MA Convergence scheduled — checkpoints at even hours :30 UTC');

    // Box vs TMA50: fires at even hours :30 in GMT+7 (8:30, 10:30, 12:30, ...)
    this._scheduleNextBoxVsTma50Cycle();
    console.log('[ChinBot] Box vs TMA50 scheduled — checkpoints at even hours :30 GMT+7');

    console.log(`[ChinBot] Started — account=${this.accountId} symbols=${this.symbolIds.length} tf=${this.timeframe}`);
    this.log('success', 'SYSTEM', `Engine STARTED: ${this.symbolIds.length} symbols streaming on ${this.timeframe}`);
    this.notify();
  }

  stop(): void {
    this.running = false;
    this.connected = false;

    // Reset TMA50 cross alert state
    this.tma50CrossAlertedTs = {};

    // Reset MA convergence alert state
    this.convergenceAlertedSmaTma.clear();
    this.convergenceAlertedAllMa.clear();

    // Reset Box alert state
    this.boxLevels = {};
    this.boxAlertedCycle = {};
    this.boxCycleKey = 0;

    // Reset Box vs TMA50 alert state
    this.boxVsTma50AlertedCycle.clear();

    // Clear throttled spot notify timer
    if (this.spotThrottleTimer) {
      clearTimeout(this.spotThrottleTimer);
      this.spotThrottleTimer = null;
    }
    this.spotDirty = false;
    this.snapshotDirty = true;
    this.cachedSnapshot = null;

    // Clear polling timers
    if (this._tma50PollTimer) {
      clearInterval(this._tma50PollTimer);
      this._tma50PollTimer = null;
      console.log('[ChinBot] TMA50 Cross polling stopped');
    }
    if (this._tma50ForceCheckTimer) {
      clearTimeout(this._tma50ForceCheckTimer);
      this._tma50ForceCheckTimer = null;
      console.log('[ChinBot] TMA50 Force Check timer stopped');
    }
    if (this._convergenceTimer) {
      clearTimeout(this._convergenceTimer);
      this._convergenceTimer = null;
      console.log('[ChinBot] MA Convergence timer stopped');
    }
    if (this._boxVsTma50Timer) {
      clearTimeout(this._boxVsTma50Timer);
      this._boxVsTma50Timer = null;
      console.log('[ChinBot] Box vs TMA50 timer stopped');
    }

    if (this.unsubStream) {
      this.unsubStream();
      this.unsubStream = null;
    }

    console.log(`[ChinBot] Stopped — account=${this.accountId}`);
    this.log('info', 'SYSTEM', 'Engine STOPPED');
    this.notify();
  }

  destroy(): void {
    this.stop();
    this.listeners.clear();
    this.buffers = {};
    this.symbolIds = [];
    this.cachedSnapshot = null;
  }
}

// ─── Per-Account Singleton Registry ─────────────────────────
// Each ChinBotEngine instance is scoped to a SINGLE cTrader account.
// A strategy can run on MULTIPLE accounts simultaneously, and each
// account needs its OWN data stream, buffer, alert anti-spam state,
// and Telegram email — sharing one engine caused account A's stream
// to be overwritten by account B (cross-account contamination).
//
// Composite key = buildSessionKey(strategyId, accountId).

const engines = new Map<string, ChinBotEngine>();

export function getOrCreateChinEngine(strategyId: string, accountId: string): ChinBotEngine {
  const key = buildSessionKey(strategyId, accountId);
  let eng = engines.get(key);
  if (!eng) {
    eng = new ChinBotEngine();
    engines.set(key, eng);
  }
  return eng;
}

export function getChinEngine(strategyId: string, accountId: string): ChinBotEngine | undefined {
  return engines.get(buildSessionKey(strategyId, accountId));
}

export function getAllChinEngines(): Map<string, ChinBotEngine> {
  return engines;
}

export function destroyChinEngine(strategyId: string, accountId: string): void {
  const key = buildSessionKey(strategyId, accountId);
  const eng = engines.get(key);
  if (eng) {
    eng.destroy();
    engines.delete(key);
  }
}